import { createApp, markRaw } from 'vue'
import { createPinia } from 'pinia'
import { createAuth0 } from '@auth0/auth0-vue'
import { Quasar, Loading, Dialog, Notify } from 'quasar'
import { FullStory, init as initFullStory } from '@fullstory/browser'
import * as Sentry from '@sentry/vue'
import { LDPlugin } from 'launchdarkly-vue-client-sdk'
import vSelect from 'vue-select'
import vuetify from './plugins/vuetify'
import formkit from './plugins/formkit'
import { createNotivue } from 'notivue'

import '@quasar/extras/material-icons/material-icons.css'
import '@quasar/extras/material-icons-outlined/material-icons-outlined.css'
import 'quasar/src/css/index.sass'
import 'vue-select/dist/vue-select.css'
import '@/styles/index.scss'
import 'notivue/notification.css'
import 'notivue/animations.css'
import '@vueform/vueform/dist/vueform.css'
import '@/styles/tailwind/index.css'
import '@vueform/builder/index.css'
import '@/styles/vueform-overrides.scss'

import { router } from './router'
import App from './App.vue'
import {vMaska} from 'maska/vue'

const pinia = createPinia()
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

const notivue = createNotivue({
  position: 'top-right',
  limit: 6,
})

const app = createApp(App)

// Decide if we should enable third-party auth / analytics integrations.
const shouldSkipAuth =
  import.meta.env.MODE === 'development' || import.meta.env.VITE_PRODUCTION_MODE === 'staging'

// Setup FullStory only when we are actually using Auth0 (i.e. production env)
if (!shouldSkipAuth) {
  initFullStory({ orgId: 'ZQ9DX' })
  app.config.globalProperties.$FullStory = FullStory
}

// const clientSideID = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID
const dsn = import.meta.env.MODE === 'development' ? '' : 'https://60e8316bec114b5a9b1b43c1f6b3af62@o105722.ingest.sentry.io/6509625'
Sentry.init({
  app,
  dsn,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracePropagationTargets: [/^https:\/\/relevize\.com\/.*/, /^https:\/\/adlp\.online\/.*/],
  tracesSampleRate: 1.0,
  tracingOptions: {
    trackComponents: true,
  },
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
})
app.use(notivue)
app.use(pinia)
app.use(router)
if (!shouldSkipAuth) {
  app.use(
    createAuth0({
      domain: import.meta.env.VITE_DOMAIN,
      clientId: import.meta.env.VITE_CLIENT_ID,
      authorizationParams: {
        redirect_uri: import.meta.env.VITE_CALLBACK_URI,
        audience: import.meta.env.VITE_AUDIENCE,
        issuer: import.meta.env.VITE_ISSUER,
        useRefreshTokens: true,
        cacheLocation: 'localstorage'
      },
    })
  )
}
app.use(vuetify)
app.use(Quasar, {
  plugins: {
    Loading,
    Dialog,
    Notify,
  },
  config: {
    loading: {},
  },
})
app.use(LDPlugin, { deferInitialization: true })
app.directive('maska', vMaska)
app.component('VSelect', vSelect)

app.use(formkit)

import Vueform from '@vueform/vueform'
import vueformConfig from './plugins/vueform.config'
import Builder from '@vueform/builder'
import builderConfig from './plugins/builder.config'

app.use(Vueform, vueformConfig)
app.use(Builder, builderConfig)

app.mount('#app')
